import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import LoginPage from "@/pages/LoginPage";
import Dashboard from "@/pages/Dashboard";
import PreviewPage from "@/pages/PreviewPage";
import PackingListEditPage from "@/pages/PackingListEditPage";
import NodeNotePage from "@/pages/NodeNotePage";
import BulkLabelExcelPage from "@/components/BulkLabelExcelPage";
import DatabaseAHistoryPage from "@/components/DatabaseAHistoryPage";
import TodayInvoicesPage from "@/pages/TodayInvoicesPage";
import PendingWorklistPage from "@/pages/PendingWorklistPage";
import OrderRegisterAuditPage from "@/pages/OrderRegisterAuditPage";
import { Toaster } from "@/components/ui/sonner";
import { useSessionGuard } from "@/hooks/useSessionGuard";
import { ReauthDialog } from "@/components/ReauthDialog";
import { PhotoReminderPopup } from "@/components/PhotoReminders";
import PortalApp from "@/portal/PortalApp";
import PortalTrackPage from "@/portal/PortalTrackPage";
import PackerPage from "@/pages/PackerPage";

const isPacker = () => { try { return !!JSON.parse(localStorage.getItem("user") || "{}").is_packer; } catch { return false; } };
const home = () => (isPacker() ? "/packer" : "/dashboard");

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Date formatting utilities - dd/mm/yyyy format
export const formatDateDDMMYYYY = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

export const formatDateTimeDDMMYYYY = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

export const getTodayDDMMYYYY = () => {
  const today = new Date();
  const day = String(today.getDate()).padStart(2, '0');
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const year = today.getFullYear();
  return `${day}/${month}/${year}`;
};

// Convert dd/mm/yyyy to yyyy-mm-dd for input[type="date"]
export const toInputDateFormat = (ddmmyyyy) => {
  if (!ddmmyyyy) return '';
  const parts = ddmmyyyy.split('/');
  if (parts.length !== 3) return ddmmyyyy;
  return `${parts[2]}-${parts[1]}-${parts[0]}`;
};

// Convert yyyy-mm-dd to dd/mm/yyyy
export const fromInputDateFormat = (yyyymmdd) => {
  if (!yyyymmdd) return '';
  const parts = yyyymmdd.split('-');
  if (parts.length !== 3) return yyyymmdd;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  useSessionGuard(isAuthenticated, setIsAuthenticated);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Verify token
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => {
          if (r?.data) localStorage.setItem('user', JSON.stringify({ ...JSON.parse(localStorage.getItem('user') || '{}'), is_packer: !!r.data.is_packer }));
          setIsAuthenticated(true);
          setLoading(false);
        })
        .catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setIsAuthenticated(false);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/portal" element={<PortalApp withToaster={false} />} />
          <Route path="/t/:token" element={<PortalTrackPage />} />
          <Route 
            path="/login" 
            element={isAuthenticated ? <Navigate to={home()} /> : <LoginPage setIsAuthenticated={setIsAuthenticated} />} 
          />
          <Route
            path="/packer"
            element={isAuthenticated ? (isPacker() ? <PackerPage setIsAuthenticated={setIsAuthenticated} /> : <Navigate to="/dashboard" />) : <Navigate to="/login" />}
          />
          <Route 
            path="/dashboard" 
            element={isAuthenticated ? (isPacker() ? <Navigate to="/packer" /> : <Dashboard setIsAuthenticated={setIsAuthenticated} />) : <Navigate to="/login" />} 
          />
          <Route 
            path="/preview" 
            element={isAuthenticated ? <PreviewPage /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/packing-list-edit" 
            element={isAuthenticated ? <PackingListEditPage /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/node-notes" 
            element={isAuthenticated ? <NodeNotePage /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/bulk-label-excel" 
            element={isAuthenticated ? <BulkLabelExcelPage /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/database-a/history" 
            element={isAuthenticated ? <DatabaseAHistoryPage /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/today-invoices" 
            element={isAuthenticated ? <TodayInvoicesPage /> : <Navigate to="/login" />} 
          />
          <Route
            path="/pending-worklist"
            element={isAuthenticated ? <PendingWorklistPage /> : <Navigate to="/login" />}
          />
          <Route
            path="/order-register/audit"
            element={isAuthenticated ? <OrderRegisterAuditPage /> : <Navigate to="/login" />}
          />
          <Route path="/" element={<Navigate to={(isAuthenticated ? home() : "/login") + window.location.search} />} />
        </Routes>
      </BrowserRouter>
      {isAuthenticated && !isPacker() && !window.location.pathname.startsWith("/portal") && <PhotoReminderPopup />}
      {isAuthenticated && <ReauthDialog onGiveUp={() => { localStorage.removeItem('token'); localStorage.removeItem('user'); setIsAuthenticated(false); }} />}
      <Toaster position="top-right" />
    </div>
  );
}

export default App;